/**
 * Shell configurable components:
 * - `extranet.sidebarMenu` — the navigation entries of the sidebar and of
 *   the mobile drawer (`SidebarMenu`), stable keys `accueil` / `contrats` /
 *   `sinistres` / `primes` / `flotte` / `pieces`, title « Menu » (published,
 *   rendered only when a `setTitle` rule sets it).
 *
 * The rest of the shell (layout, sidebar brand header + collapse toggle, top
 * bar, auth guard, login page) stays pure chrome — not registered.
 *
 * The registration goes through `registerOnce` (idempotent) with the base
 * rule-context schema, and the module exports the `withComponentRules(…)`
 * wrapper `ExtranetSidebar` renders (desktop and drawer).
 */

import { createElement } from 'react';
import { z } from 'zod';

import { withComponentRules } from '@components/shared/configurable';

import { extranetBaseContextSchema } from '../../contextVariables';
import { EXTRANET_MODULE, registerOnce } from '../registerHelpers';
import { SidebarMenu, SidebarMenuSkeleton, type SidebarMenuProps } from './SidebarMenu';
import { SIDEBAR_MENU_ID } from './sidebarMenuConfig';

export { SIDEBAR_MENU_ID } from './sidebarMenuConfig';

// ---------------------------------------------------------------------------
// Props schema (data props only — `_layoutOverrides` is an injected meta prop,
// `onNavigate` a callback)
// ---------------------------------------------------------------------------

/** Props of `extranet.sidebarMenu` tracked by the registry. */
export type SidebarMenuRegistrationProps = Pick<SidebarMenuProps, 'collapsed'>;

export const sidebarMenuPropsSchema = z.object({
  collapsed: z.boolean().optional(),
}) satisfies z.ZodType<SidebarMenuRegistrationProps>;

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/** Register the shell components. Idempotent. */
export function registerShellComponents(): void {
  registerOnce<SidebarMenuRegistrationProps>({
    id: SIDEBAR_MENU_ID,
    displayName: 'Menu de navigation',
    module: EXTRANET_MODULE,
    component: SidebarMenu,
    propsSchema: sidebarMenuPropsSchema,
    defaultPropLabels: {
      collapsed: 'Menu réduit (icônes seules)',
    },
    previewProps: { collapsed: false },
    contextVariables: extranetBaseContextSchema,
  });
}

// Eager registration on import (idempotent — HMR / test re-imports are safe).
registerShellComponents();

/**
 * Rule-aware menu — `ExtranetSidebar` renders it inside its `<Sidebar>` root
 * (desktop and mobile drawer). While the rules load, a menu-sized skeleton
 * replaces the default 120px block.
 */
export const SidebarMenuConfigurable = withComponentRules(SidebarMenu, SIDEBAR_MENU_ID, {
  loadingFallback: createElement(SidebarMenuSkeleton),
});
